/**
 * React Query aborts the previous fetch on unmount, navigation, and replaced
 * refetch. Capture that signal synchronously so `apiFetch` GETs cancel without
 * every queryFn passing `{ signal }` by hand. Mutations must not use this.
 */

let currentQueryAbortSignal: AbortSignal | undefined;

export function takeHubQueryAbortSignal(): AbortSignal | undefined {
  return currentQueryAbortSignal;
}

export function bindHubQueryAbortSignal<T>(signal: AbortSignal | undefined, run: () => T): T {
  const previous = currentQueryAbortSignal;
  currentQueryAbortSignal = signal;
  try {
    const result = run();
    if (result != null && typeof (result as { then?: unknown }).then === "function") {
      return (Promise.resolve(result) as Promise<T>).finally(() => {
        if (currentQueryAbortSignal === signal) {
          currentQueryAbortSignal = previous;
        }
      }) as T;
    }
    currentQueryAbortSignal = previous;
    return result;
  } catch (error) {
    currentQueryAbortSignal = previous;
    throw error;
  }
}

export function resetHubQueryAbortSignalForTests(): void {
  currentQueryAbortSignal = undefined;
}

export function mergeAbortSignals(
  ...signals: Array<AbortSignal | undefined>
): AbortSignal | undefined {
  const present = signals.filter((signal): signal is AbortSignal => signal != null);
  if (present.length === 0) {
    return undefined;
  }
  if (present.length === 1) {
    return present[0];
  }
  const anyFactory = (AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal })
    .any;
  if (typeof anyFactory === "function") {
    return anyFactory(present);
  }
  const controller = new AbortController();
  for (const signal of present) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return controller.signal;
}
